import React, { useState, useRef, useEffect } from 'react';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://qorenova-backend-production.up.railway.app';

/* ─── Design tokens ─────────────────────────────────────────────────────── */
const T = {
  bg:       '#09090b',
  card:     'rgba(255,255,255,0.025)',
  border:   '1px solid rgba(255,255,255,0.08)',
  radius:   '12px',
  text:     '#fafafa',
  muted:    '#a1a1aa',
  faint:    '#71717a',
  accent:   '#6366f1',
  accentHover: '#818cf8',
};

const cardStyle = {
  background: T.card,
  border: T.border,
  borderRadius: T.radius,
  padding: '28px 24px',
};

const StrategyPlanning = () => {
  const navigate = useNavigate();
  const { isAuthenticated, token } = useAuth();

  // AI Chat State
  const [chatOpen, setChatOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const chatEndRef = useRef(null);

  // AI Assessment State
  const [assessmentStep, setAssessmentStep] = useState(0);
  const [assessmentAnswers, setAssessmentAnswers] = useState({});
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  // Goal Generation State
  const [careerGoal, setCareerGoal] = useState('');
  const [generatedPath, setGeneratedPath] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const assessmentQuestions = [
    { id: 'current_role', question: "What's your current role or situation?", type: 'text', placeholder: 'e.g., Junior Developer, Student, Career Changer...' },
    { id: 'experience', question: 'How many years of professional experience do you have?', type: 'select', options: ['0-1 years', '2-4 years', '5-9 years', '10+ years'] },
    { id: 'goal', question: "What's your career goal for the next 2 years?", type: 'text', placeholder: 'e.g., Become a Team Lead, Switch to Product Management...' },
    { id: 'challenge', question: "What's your biggest professional challenge right now?", type: 'select', options: ['Communication with stakeholders', 'Leading and managing others', 'Strategic thinking and planning', 'Work-life balance', 'Getting promoted', 'Building confidence'] },
    { id: 'learning_style', question: 'How do you prefer to learn?', type: 'select', options: ['Interactive exercises and practice', 'Reading and self-study', 'Video content', 'Live coaching sessions', 'Learning by doing real projects'] },
  ];

  const sendToAI = async (userMessage) => {
    if (!token) return { content: 'Please sign in to chat with the AI coach.' };
    try {
      const response = await fetch(`${API_BASE}/api/ai/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          messages: [
            { role: 'system', content: 'You are an expert professional development coach specializing in soft skills, leadership, communication, and career growth. Provide actionable, personalized advice (2-3 paragraphs max). Be encouraging but realistic.' },
            ...messages.map(m => ({ role: m.role, content: m.content })),
            { role: 'user', content: userMessage },
          ],
          taskType: 'coaching',
          context: { feature: 'professional_development' },
        }),
      });
      const data = await response.json();
      return data.success ? data.data : { content: "I couldn't process that. Please try again." };
    } catch {
      return { content: 'Connection error. Please check your internet and try again.' };
    }
  };

  const handleChatSubmit = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || isTyping) return;
    const userMsg = { role: 'user', content: inputMessage };
    setMessages(prev => [...prev, userMsg]);
    setInputMessage('');
    setIsTyping(true);
    const aiResponse = await sendToAI(inputMessage);
    setMessages(prev => [...prev, { role: 'assistant', content: aiResponse.content }]);
    setIsTyping(false);
  };

  const quickPrompts = [
    'How can I improve my communication skills?',
    'What skills do I need to become a manager?',
    'How do I handle difficult conversations at work?',
    'Help me prepare for a promotion discussion',
  ];

  const handleAssessmentAnswer = (answer) => {
    setAssessmentAnswers(prev => ({ ...prev, [assessmentQuestions[assessmentStep].id]: answer }));
    if (assessmentStep < assessmentQuestions.length - 1) {
      setAssessmentStep(prev => prev + 1);
    } else {
      analyzeWithAI({ ...assessmentAnswers, [assessmentQuestions[assessmentStep].id]: answer });
    }
  };

  const analyzeWithAI = async (answers) => {
    setIsAnalyzing(true);
    if (!token) {
      setAiAnalysis({ summary: 'Sign in to get your personalized AI analysis', strengths: ['Complete the assessment after signing in'], growthAreas: ['Your personalized growth areas will appear here'], recommendedPath: 'leadership', nextSteps: ['Create an account to unlock AI-powered insights'] });
      setIsAnalyzing(false);
      return;
    }
    try {
      const response = await fetch(`${API_BASE}/api/ai/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          messages: [{ role: 'user', content: `Analyze this professional development assessment. Return ONLY valid JSON.\n\nAssessment:\n- Role: ${answers.current_role}\n- Experience: ${answers.experience}\n- Goal: ${answers.goal}\n- Challenge: ${answers.challenge}\n- Learning Style: ${answers.learning_style}\n\nFormat:\n{"summary":"...","strengths":["..."],"growthAreas":["..."],"recommendedPath":"leadership","nextSteps":["..."]}` }],
          taskType: 'assessment',
        }),
      });
      const data = await response.json();
      if (data.success && data.data?.content) {
        try {
          const match = data.data.content.match(/\{[\s\S]*\}/);
          if (match) setAiAnalysis(JSON.parse(match[0]));
        } catch {
          setAiAnalysis({ summary: 'Analysis complete.', strengths: [], growthAreas: [], nextSteps: [] });
        }
      }
    } catch {
      setAiAnalysis({ summary: 'Unable to analyze at this time.', strengths: [], growthAreas: [], nextSteps: [] });
    }
    setIsAnalyzing(false);
  };

  const generateLearningPath = async () => {
    if (!careerGoal.trim()) return;
    setIsGenerating(true);
    setGeneratedPath(null);
    if (!token) {
      setGeneratedPath({ targetRole: careerGoal, skills: [{ name: 'Communication', priority: 'high', estimatedHours: 20 }, { name: 'Leadership', priority: 'high', estimatedHours: 30 }, { name: 'Project Management', priority: 'medium', estimatedHours: 25 }], estimatedWeeks: 12 });
      setIsGenerating(false);
      return;
    }
    try {
      const response = await fetch(`${API_BASE}/api/ai/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          messages: [{ role: 'user', content: `Create a soft skills learning path for: "${careerGoal}"\nReturn ONLY JSON:\n{"targetRole":"...","skills":[{"name":"...","priority":"high","estimatedHours":20,"why":"..."}],"estimatedWeeks":12,"weeklyCommitment":"5-7 hours","milestones":[{"week":4,"achievement":"..."}],"tips":["..."]}` }],
          taskType: 'path_generation',
          options: { temperature: 0.4 },
        }),
      });
      const data = await response.json();
      if (data.success && data.data?.content) {
        try {
          const match = data.data.content.match(/\{[\s\S]*\}/);
          if (match) setGeneratedPath(JSON.parse(match[0]));
        } catch {
          /* silent */
        }
      }
    } catch {
      /* silent */
    }
    setIsGenerating(false);
  };

  const learningPaths = [
    { id: 'communication', icon: '💬', title: 'Communication Excellence', description: 'Master clear, persuasive communication for any professional setting.', skills: ['Public Speaking', 'Active Listening', 'Presentations', 'Written Communication'], color: '#8b5cf6', aiFeature: 'AI-powered speech analysis & feedback' },
    { id: 'leadership', icon: '👑', title: 'Leadership & Management', description: 'Develop the mindset and skills to lead teams effectively.', skills: ['Team Building', 'Decision Making', 'Conflict Resolution', 'Delegation'], color: '#f59e0b', aiFeature: 'AI leadership style assessment' },
    { id: 'project-management', icon: '📊', title: 'Project Management', description: 'Plan, execute, and deliver projects successfully.', skills: ['Agile & Scrum', 'Risk Management', 'Stakeholder Management', 'Planning'], color: '#06b6d4', aiFeature: 'AI project planning assistant' },
    { id: 'emotional-intelligence', icon: '🧠', title: 'Emotional Intelligence', description: 'Build self-awareness and stronger professional relationships.', skills: ['Self-Awareness', 'Empathy', 'Social Skills', 'Stress Management'], color: '#ec4899', aiFeature: 'AI emotional pattern recognition' },
    { id: 'strategic-thinking', icon: '🎯', title: 'Strategic Thinking', description: 'Think critically and make impactful decisions.', skills: ['Problem Solving', 'Critical Analysis', 'Goal Setting', 'Business Acumen'], color: '#10b981', aiFeature: 'AI strategy simulation exercises' },
    { id: 'career-growth', icon: '🚀', title: 'Career Acceleration', description: 'Navigate your career path with confidence.', skills: ['Personal Branding', 'Networking', 'Interview Skills', 'Negotiation'], color: '#6366f1', aiFeature: 'AI career path optimization' },
  ];

  const services = [
    { icon: '🧭', title: 'Career Strategy', desc: 'Craft a clear, personalized roadmap toward your professional goals.' },
    { icon: '📈', title: 'Leadership Development', desc: 'Build the executive presence and decision-making skills to lead.' },
    { icon: '🤝', title: 'Stakeholder Alignment', desc: 'Navigate complex organizational dynamics with confidence.' },
    { icon: '⚙️', title: 'Operational Excellence', desc: 'Streamline how you work and maximize your professional output.' },
    { icon: '🌐', title: 'Global Communication', desc: 'Communicate with clarity and influence across borders and teams.' },
    { icon: '��', title: 'Future-Readiness', desc: 'Stay ahead of industry shifts with adaptive skills and mindset.' },
  ];

  const processSteps = [
    { num: '01', label: 'Assess', desc: 'AI-powered skills assessment maps your strengths and gaps in minutes.' },
    { num: '02', label: 'Plan', desc: 'A personalized learning roadmap built around your goals and timeline.' },
    { num: '03', label: 'Execute', desc: 'Guided modules, live coaching, and AI feedback drive real transformation.' },
  ];

  return (
    <div style={{ minHeight: '100vh', backgroundColor: T.bg, color: T.text, fontFamily: 'Inter, sans-serif' }}>
      <Header />

      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <section style={{ padding: '96px 24px 80px', textAlign: 'center', background: 'linear-gradient(180deg, rgba(99,102,241,0.08) 0%, transparent 100%)' }}>
        <div style={{ maxWidth: 720, margin: '0 auto' }}>
          <span style={{ display: 'inline-block', padding: '6px 16px', border: '1px solid rgba(99,102,241,0.4)', borderRadius: 20, fontSize: 13, color: T.accentHover, marginBottom: 24, letterSpacing: '0.05em' }}>
            AI-Powered Professional Development
          </span>
          <h1 style={{ fontSize: 'clamp(2.4rem,5vw,3.6rem)', fontWeight: 700, margin: '0 0 20px', lineHeight: 1.15, letterSpacing: '-0.02em' }}>
            Strategic Planning<br />
            <span style={{ color: T.accentHover }}>for Your Career</span>
          </h1>
          <p style={{ fontSize: '1.15rem', color: T.muted, marginBottom: 40, lineHeight: 1.7 }}>
            Personalised coaching, AI-driven assessments and structured learning paths that turn ambitious goals into measurable results.
          </p>
          <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => setChatOpen(true)}
              style={{ padding: '14px 32px', background: T.accent, color: '#fff', border: 'none', borderRadius: T.radius, fontSize: 15, fontWeight: 600, cursor: 'pointer' }}
            >
              Talk to AI Coach
            </button>
            <button
              onClick={() => document.getElementById('sp-assessment')?.scrollIntoView({ behavior: 'smooth' })}
              style={{ padding: '14px 32px', background: 'transparent', color: T.text, border: T.border, borderRadius: T.radius, fontSize: 15, fontWeight: 500, cursor: 'pointer' }}
            >
              Take Free Assessment
            </button>
          </div>
        </div>
      </section>

      {/* ── Services ─────────────────────────────────────────────────────── */}
      <section style={{ padding: '72px 24px', maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 56 }}>
          <h2 style={{ fontSize: '2rem', fontWeight: 700, margin: 0 }}>Planning Services</h2>
          <p style={{ color: T.muted, marginTop: 12 }}>Six disciplines designed to accelerate your professional trajectory.</p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 20 }}>
          {services.map(s => (
            <div key={s.title} style={cardStyle}>
              <div style={{ fontSize: 32, marginBottom: 16 }}>{s.icon}</div>
              <h3 style={{ margin: '0 0 8px', fontSize: '1.05rem', fontWeight: 600 }}>{s.title}</h3>
              <p style={{ margin: 0, color: T.muted, fontSize: '0.9rem', lineHeight: 1.6 }}>{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── 3-Step Process ───────────────────────────────────────────────── */}
      <section style={{ padding: '72px 24px', background: 'rgba(255,255,255,0.015)' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{ fontSize: '2rem', fontWeight: 700, marginBottom: 12 }}>How It Works</h2>
          <p style={{ color: T.muted, marginBottom: 56 }}>Three focused steps from where you are to where you want to be.</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 24, alignItems: 'start' }}>
            {processSteps.map((step, idx) => (
              <div key={step.label} style={{ position: 'relative' }}>
                {idx < processSteps.length - 1 && (
                  <div style={{ display: 'none' }} />
                )}
                <div style={{ ...cardStyle, textAlign: 'left' }}>
                  <div style={{ fontSize: '2rem', fontWeight: 800, color: T.accent, marginBottom: 12 }}>{step.num}</div>
                  <h3 style={{ margin: '0 0 8px', fontSize: '1.1rem', fontWeight: 700 }}>{step.label}</h3>
                  <p style={{ margin: 0, color: T.muted, fontSize: '0.9rem', lineHeight: 1.65 }}>{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── AI Path Generator ────────────────────────────────────────────── */}
      <section style={{ padding: '72px 24px', maxWidth: 800, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <h2 style={{ fontSize: '1.8rem', fontWeight: 700, margin: '0 0 10px' }}>Generate Your Learning Path</h2>
          <p style={{ color: T.muted }}>Describe your goal — our AI builds a personalised roadmap instantly.</p>
        </div>
        <div style={{ ...cardStyle, padding: 32 }}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <input
              type="text"
              placeholder="e.g., I want to become a Product Manager in 6 months"
              value={careerGoal}
              onChange={e => setCareerGoal(e.target.value)}
              onKeyPress={e => e.key === 'Enter' && generateLearningPath()}
              style={{ flex: 1, minWidth: 240, padding: '12px 16px', background: 'rgba(255,255,255,0.05)', border: T.border, borderRadius: 8, color: T.text, fontSize: 14, outline: 'none' }}
            />
            <button
              onClick={generateLearningPath}
              disabled={isGenerating || !careerGoal.trim()}
              style={{ padding: '12px 28px', background: isGenerating || !careerGoal.trim() ? 'rgba(99,102,241,0.4)' : T.accent, color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: isGenerating || !careerGoal.trim() ? 'not-allowed' : 'pointer' }}
            >
              {isGenerating ? 'Generating…' : 'Generate Path'}
            </button>
          </div>

          {generatedPath && (
            <div style={{ marginTop: 28, paddingTop: 28, borderTop: T.border }}>
              <h3 style={{ margin: '0 0 8px', fontSize: '1rem', fontWeight: 700 }}>🎯 {generatedPath.targetRole}</h3>
              {generatedPath.estimatedWeeks && <p style={{ color: T.muted, fontSize: 13, margin: '0 0 16px' }}>{generatedPath.estimatedWeeks} weeks · {generatedPath.weeklyCommitment || '5-7 hrs/week'}</p>}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {generatedPath.skills?.map((skill, i) => (
                  <span key={i} style={{ padding: '6px 14px', background: 'rgba(99,102,241,0.15)', border: '1px solid rgba(99,102,241,0.3)', borderRadius: 20, fontSize: 13, color: T.accentHover }}>
                    {skill.name} {skill.estimatedHours ? `· ${skill.estimatedHours}h` : ''}
                  </span>
                ))}
              </div>
              {!isAuthenticated && (
                <button onClick={() => navigate('/register')} style={{ marginTop: 20, width: '100%', padding: '12px', background: T.accent, color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                  Sign Up to Save Your Path
                </button>
              )}
            </div>
          )}
        </div>
      </section>

      {/* ── AI Assessment ────────────────────────────────────────────────── */}
      <section id="sp-assessment" style={{ padding: '72px 24px', background: 'rgba(255,255,255,0.015)' }}>
        <div style={{ maxWidth: 700, margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{ fontSize: '1.8rem', fontWeight: 700, margin: '0 0 10px' }}>AI Skills Assessment</h2>
          <p style={{ color: T.muted, marginBottom: 48 }}>Answer 5 quick questions and get personalised AI insights.</p>

          <div style={{ ...cardStyle, padding: 40, textAlign: 'left' }}>
            {!aiAnalysis ? (
              <>
                <div style={{ height: 4, background: 'rgba(255,255,255,0.07)', borderRadius: 2, marginBottom: 8 }}>
                  <div style={{ height: '100%', width: `${((assessmentStep + 1) / assessmentQuestions.length) * 100}%`, background: T.accent, borderRadius: 2, transition: 'width 0.4s' }} />
                </div>
                <p style={{ color: T.faint, fontSize: 13, marginBottom: 28 }}>Question {assessmentStep + 1} of {assessmentQuestions.length}</p>
                <h3 style={{ fontSize: '1.05rem', fontWeight: 600, marginBottom: 24 }}>{assessmentQuestions[assessmentStep].question}</h3>
                {assessmentQuestions[assessmentStep].type === 'text' ? (
                  <div style={{ display: 'flex', gap: 10 }}>
                    <input
                      type="text"
                      placeholder={assessmentQuestions[assessmentStep].placeholder}
                      style={{ flex: 1, padding: '11px 16px', background: 'rgba(255,255,255,0.05)', border: T.border, borderRadius: 8, color: T.text, fontSize: 14, outline: 'none' }}
                      onKeyPress={e => { if (e.key === 'Enter' && e.target.value.trim()) { handleAssessmentAnswer(e.target.value); e.target.value = ''; } }}
                    />
                    <button
                      onClick={e => { const inp = e.target.previousSibling; if (inp.value.trim()) { handleAssessmentAnswer(inp.value); inp.value = ''; } }}
                      style={{ padding: '11px 22px', background: T.accent, color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, cursor: 'pointer' }}
                    >
                      Next →
                    </button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {assessmentQuestions[assessmentStep].options.map((opt, i) => (
                      <button
                        key={i}
                        onClick={() => handleAssessmentAnswer(opt)}
                        style={{ padding: '12px 18px', textAlign: 'left', background: 'rgba(255,255,255,0.04)', border: T.border, borderRadius: 8, color: T.text, fontSize: 14, cursor: 'pointer' }}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                )}
              </>
            ) : isAnalyzing ? (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <div style={{ fontSize: 40, marginBottom: 16 }}>🤖</div>
                <p style={{ color: T.muted }}>AI is analysing your responses…</p>
              </div>
            ) : (
              <div>
                <h3 style={{ margin: '0 0 16px', fontSize: '1.1rem', fontWeight: 700 }}>✨ Your AI Analysis</h3>
                <p style={{ color: T.muted, lineHeight: 1.7, marginBottom: 24 }}>{aiAnalysis.summary}</p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
                  <div style={{ ...cardStyle, padding: 20 }}>
                    <h4 style={{ margin: '0 0 12px', fontSize: '0.9rem', color: '#34d399' }}>💪 Strengths</h4>
                    <ul style={{ margin: 0, paddingLeft: 18, color: T.muted, fontSize: 13, lineHeight: 1.8 }}>
                      {aiAnalysis.strengths?.map((s, i) => <li key={i}>{s}</li>)}
                    </ul>
                  </div>
                  <div style={{ ...cardStyle, padding: 20 }}>
                    <h4 style={{ margin: '0 0 12px', fontSize: '0.9rem', color: '#f59e0b' }}>🎯 Growth Areas</h4>
                    <ul style={{ margin: 0, paddingLeft: 18, color: T.muted, fontSize: 13, lineHeight: 1.8 }}>
                      {aiAnalysis.growthAreas?.map((g, i) => <li key={i}>{g}</li>)}
                    </ul>
                  </div>
                </div>
                {aiAnalysis.nextSteps?.length > 0 && (
                  <div style={{ marginBottom: 28 }}>
                    <h4 style={{ margin: '0 0 12px', fontSize: '0.9rem', color: T.accentHover }}>🚀 Next Steps</h4>
                    <ol style={{ margin: 0, paddingLeft: 20, color: T.muted, fontSize: 13, lineHeight: 1.9 }}>
                      {aiAnalysis.nextSteps.map((step, i) => <li key={i}>{step}</li>)}
                    </ol>
                  </div>
                )}
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <button onClick={() => { setAiAnalysis(null); setAssessmentStep(0); setAssessmentAnswers({}); }} style={{ flex: 1, minWidth: 140, padding: '11px 20px', background: 'transparent', color: T.text, border: T.border, borderRadius: 8, fontSize: 14, cursor: 'pointer' }}>
                    Retake Assessment
                  </button>
                  <button onClick={() => isAuthenticated ? navigate('/dashboard') : navigate('/register')} style={{ flex: 1, minWidth: 140, padding: '11px 20px', background: T.accent, color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                    Start Learning
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── Learning Paths ───────────────────────────────────────────────── */}
      <section style={{ padding: '72px 24px', maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <h2 style={{ fontSize: '1.8rem', fontWeight: 700, margin: '0 0 10px' }}>AI-Enhanced Learning Paths</h2>
          <p style={{ color: T.muted }}>Each path features personalised AI coaching and adaptive content.</p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 20 }}>
          {learningPaths.map(path => (
            <div key={path.id} style={{ ...cardStyle, borderTop: `2px solid ${path.color}40` }}>
              <div style={{ fontSize: 32, marginBottom: 14 }}>{path.icon}</div>
              <h3 style={{ margin: '0 0 8px', fontSize: '1rem', fontWeight: 600 }}>{path.title}</h3>
              <p style={{ margin: '0 0 16px', color: T.muted, fontSize: '0.875rem', lineHeight: 1.6 }}>{path.description}</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
                {path.skills.map((sk, i) => (
                  <span key={i} style={{ padding: '3px 10px', background: `${path.color}18`, border: `1px solid ${path.color}40`, borderRadius: 12, fontSize: 11, color: path.color }}>
                    {sk}
                  </span>
                ))}
              </div>
              <p style={{ margin: '0 0 18px', fontSize: 12, color: T.faint }}>🤖 {path.aiFeature}</p>
              <button
                onClick={() => isAuthenticated ? navigate('/dashboard') : navigate('/register')}
                style={{ width: '100%', padding: '10px', background: 'transparent', color: T.accentHover, border: `1px solid ${T.accent}`, borderRadius: 8, fontSize: 14, cursor: 'pointer' }}
              >
                Start Learning →
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* ── CTA Banner ───────────────────────────────────────────────────── */}
      <section style={{ padding: '80px 24px', background: 'linear-gradient(135deg, rgba(99,102,241,0.12) 0%, rgba(139,92,246,0.08) 100%)', borderTop: T.border, borderBottom: T.border }}>
        <div style={{ maxWidth: 640, margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{ fontSize: '2rem', fontWeight: 700, margin: '0 0 16px' }}>Ready to transform your strategy?</h2>
          <p style={{ color: T.muted, marginBottom: 36, lineHeight: 1.7 }}>
            Join thousands of professionals who are accelerating their careers with AI-powered coaching and strategic planning.
          </p>
          <button
            onClick={() => navigate('/contact')}
            style={{ padding: '15px 40px', background: T.accent, color: '#fff', border: 'none', borderRadius: T.radius, fontSize: 16, fontWeight: 600, cursor: 'pointer' }}
          >
            Get Started Today
          </button>
        </div>
      </section>

      {/* ── AI Chat Modal ────────────────────────────────────────────────── */}
      {chatOpen && (
        <div
          onClick={() => setChatOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end', padding: 24 }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ width: '100%', maxWidth: 440, height: 540, background: '#111113', border: T.border, borderRadius: 16, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
          >
            {/* Chat header */}
            <div style={{ padding: '16px 20px', borderBottom: T.border, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontSize: 28 }}>🤖</span>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>AI Career Coach</div>
                  <div style={{ fontSize: 12, color: '#34d399' }}>Online · Powered by AI</div>
                </div>
              </div>
              <button onClick={() => setChatOpen(false)} style={{ background: 'none', border: 'none', color: T.muted, fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>

            {/* Messages */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
              {messages.length === 0 && (
                <div style={{ textAlign: 'center', paddingTop: 20 }}>
                  <div style={{ fontSize: 36, marginBottom: 12 }}>🤖</div>
                  <h3 style={{ margin: '0 0 8px', fontSize: '1rem' }}>Hi! I'm your AI Career Coach</h3>
                  <p style={{ color: T.muted, fontSize: 13, marginBottom: 20 }}>Ask me anything about professional development, soft skills, or career growth.</p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {quickPrompts.map((p, i) => (
                      <button key={i} onClick={() => setInputMessage(p)} style={{ padding: '8px 14px', background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.3)', borderRadius: 8, color: T.accentHover, fontSize: 13, cursor: 'pointer', textAlign: 'left' }}>
                        {p}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((msg, idx) => (
                <div key={idx} style={{ marginBottom: 16, display: 'flex', gap: 10, flexDirection: msg.role === 'user' ? 'row-reverse' : 'row' }}>
                  {msg.role === 'assistant' && <span style={{ fontSize: 20, flexShrink: 0 }}>🤖</span>}
                  <div style={{ maxWidth: '80%', padding: '10px 14px', background: msg.role === 'user' ? T.accent : 'rgba(255,255,255,0.06)', borderRadius: 10, fontSize: 13, lineHeight: 1.65, color: T.text }}>
                    {msg.content}
                  </div>
                </div>
              ))}
              {isTyping && (
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <span style={{ fontSize: 20 }}>🤖</span>
                  <div style={{ padding: '10px 16px', background: 'rgba(255,255,255,0.06)', borderRadius: 10, color: T.muted, fontSize: 13 }}>Typing…</div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>

            {/* Input */}
            <form onSubmit={handleChatSubmit} style={{ padding: '12px 16px', borderTop: T.border, display: 'flex', gap: 10 }}>
              <input
                type="text"
                placeholder="Ask your AI coach anything…"
                value={inputMessage}
                onChange={e => setInputMessage(e.target.value)}
                disabled={isTyping}
                style={{ flex: 1, padding: '10px 14px', background: 'rgba(255,255,255,0.05)', border: T.border, borderRadius: 8, color: T.text, fontSize: 14, outline: 'none' }}
              />
              <button type="submit" disabled={isTyping || !inputMessage.trim()} style={{ padding: '10px 18px', background: T.accent, color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, cursor: isTyping || !inputMessage.trim() ? 'not-allowed' : 'pointer', opacity: isTyping || !inputMessage.trim() ? 0.5 : 1 }}>
                Send
              </button>
            </form>
          </div>
        </div>
      )}

      {/* ── FAB ──────────────────────────────────────────────────────────── */}
      {!chatOpen && (
        <button
          onClick={() => setChatOpen(true)}
          style={{ position: 'fixed', bottom: 28, right: 28, width: 56, height: 56, borderRadius: '50%', background: T.accent, border: 'none', fontSize: 24, cursor: 'pointer', boxShadow: '0 8px 24px rgba(99,102,241,0.4)', zIndex: 900 }}
        >
          🤖
        </button>
      )}

      <Footer />
    </div>
  );
};

export default StrategyPlanning;
